import React from 'react';

const Documentation = () => {
    return (
        <div className="documentation">
			<h5 className="documentation-header">About</h5>
			<p>This tool creates questions pools for exams in Blackboard. It allows typing a list of questions
				in a program such as Word or Notepad as an alternative to the Blackboard interface.</p>
			<h5 className="documentation-header">How To Use</h5>
			<ol>
				<li>Using the question type format as listed below, copy and paste your questions into the provided <b>Questions Data</b> field on the right.</li>
				<li>Fill out the rest of form as needed and click <b>Generate Question Pool</b>. Download/save the zip file in a location on your computer.</li>
				<li>Login to <a href="https://vuws.westernsydney.edu.au/" target="_blank" rel="noopener noreferrer">vUWS</a>.</li>
				<li>Open the vUWS site that will use the question pool.</li>
				<li>On the left-hand navigation pane of your vUWS site, under <b>Course Management</b> expand <b>Course Tools</b> and select <b>Tests, Surveys and Pools</b>.
					<br/>
					<img src="img/blackboard-menu.png"/>
				</li>
				<li>On the right-hand content pane of your vUWS site, <i>Tests, Surveys and Pools</i> will appear. Select the <b>Pools</b> link.
					<br/>
					<img src="img/blackboard-menu-tools.png"/>
				</li>
				<li>The content pane will now show <i>Pools</i>, select the <b>Import Pool</b> button from the action bar.
					<br/>
					<img src="img/blackboard-pools-import.png"/>
				</li>
				<li>On the <i>Pool Import</i> page, select <b>Browse My Computer</b>, select the downloaded zip file and click Submit on the page.
					<br/>
					<img src="img/blackboard-pool-file.png"/>
				</li>
				<li>Blackboard should show the message <b>Pool Import Complete</b>, select <b>OK</b> in the bottom right-hand corner.
					<br/>
					<img src="img/blackboard-pool-complete.png"/>
				</li>
				<li>Your question pool is now available to be used to create a Blackboard Test.</li>
			</ol>
	
			<h5 className="documentation-header">General Formatting Rules</h5>
			<ul>
				<li>Questions start with a <b>number</b> followed by a <b>period or parenthesis</b> (e.g. "1.", "1)"). A question can only exist on a <b>single line</b>.</li>
				<li>Answers start with a <b>letter or number</b> followed by a <b>period or parenthesis</b> (e.g. "a.", "A.", "a)", "A)"). An answer can only exist on a <b>single line</b>.</li>
				<li>Letters are not case sensitive (can be lower or upper case).</li>
				<li>
					Don't use newlines or return characters except when you're going on to the next question or answer. This
					is especially important if you're pasting in questions from another program like Word.
				</li>
				<li>Separate your questions with a blank line.</li>
				<li>HTML formatting is supported (e.g. bold, italic, underline, br tags)</li>
			</ul>

			<h5 className="documentation-header">Question Types</h5>
			<ul>
				<li><a href="#mc">Multiple Choice</a></li>
				<li><a href="#ma">Multiple Answer</a></li>
				<li><a href="#tf">True/False</a></li>
				<li><a href="#essay">Essay</a></li>
				<li><a href="#blank">Fill in the blank</a></li>
				<li><a href="#match">Matching</a></li>
			</ul>
			<p className="question_type" id="mc"><strong>Multiple Choice</strong></p>
			<div class="layout">
				<div class="item">
					<ul>
						<li>Question on a single line.</li>
						<li>Answers immediately following the question.</li>
						<li>Asterisk (*) in front of the correct choice.</li>
					</ul>
				</div>
				<div class="example">
					<div class="example-text">
					1. Which of the following is a prime number?<br/>
					a) 4<br/>
					*b) 5<br/>
					c) 6
					</div>
				</div>
			</div>
			<p class="question_type" id="ma"><strong>Multiple Answer</strong></p>
			<div class="layout">
				<div class="item">
					<ul>
						<li>Question on a single line.</li>
						<li>Exactly the same as multiple choice, only with multiple answers marked correct. </li>
						<li>Answers immediately following the question.</li>
						<li>Asterisk (*) in front of all correct choice.</li>
					</ul>
				</div>
				<div class="example">
					<div class="example-text">
					1. Which of the following is a prime number?<br/>
					*a) 2 <br/>
					*b)
					3<br/>
					d) 4<br/>
					*e) 5<br/>
					f) 6 <br/>
					*g) 7
					</div>
				</div>
			</div>
			<p class="question_type" id="tf"><strong>True/False</strong></p>
			<div class="layout">
				<div class="item">
					<ul>
						<li>Question on a single line</li>
						<li>
							The answer on the next line. Can be any of the following: T, t, True, TRUE, true, F, f, False, FALSE,
							false
						</li>
					</ul>
				</div>
				<div class="example">
					<div class="example-text">
					1. 3 is a prime number. <br/>
					True
					</div>
				</div>
			</div>
			<p class="question_type" id="essay"><strong>Essay</strong></p>
			<div class="layout">
				<div class="item">
					<ul>
						<li>This is simply a question with no answer given.</li>
					</ul>
				</div>
				<div class="example">
					<div class="example-text">
					1. Tell me your life story.
					</div>
				</div>
			</div>
			<p class="question_type" id="blank"><strong>Fill in the blank</strong></p>
			<div class="layout">
				<div class="item">
					<ul>
						<li>Begin your question with the keyword: blank </li>
						<li>Leave a blank somewhere in the question.</li>
						<li>Give all of the possible correct answers.</li>
					</ul>
				</div>
				<div class="example">
					<div class="example-text">
					blank 1. Two plus two equals _____.<br/>
					a. four<br/>
					b. 4
					</div>
				</div>
			</div>
			<p class="question_type" id="match"><strong>Matching</strong> </p>
			<div class="layout">
				<div class="item">
					<ul>
						<li>Begin your question with the keyword: match</li>
						<li>Put the matches separated by slashes (/).</li>
						<li>You can have entries with no match, i.e. leave the other side blank. </li>
						<li>The entries will be randomized.</li>
					</ul>
				</div>
				<div class="example">
					<div class="example-text">
					match 1. Match the number with its spelling.<br/>
					a. 3 / three<br/>
					b. 1 / one<br/>
					c. 12 / twelve<br/>
					d. 4 /<br/>
					e.
					/ fore
					</div>
				</div>
            </div>
        </div>
    )
  }

  export default Documentation;
